import { useEffect, useRef, useState } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { X, ZoomIn } from "lucide-react";

const galleryImages = [
  {
    src: "https://images.pexels.com/photos/11896310/pexels-photo-11896310.jpeg",
    title: "Facial Treatment",
    category: "Treatments",
  },
  {
    src: "https://images.unsplash.com/photo-1731515067388-d5df113d4326?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxsZWQlMjBsaWdodCUyMHRoZXJhcHklMjBmYWNpYWx8ZW58MHx8fHwxNzc1MzYyMDk2fDA&ixlib=rb-4.1.0&q=85",
    title: "LED Therapy",
    category: "Technology",
  },
  {
    src: "https://images.unsplash.com/photo-1771955216611-0a826d819978?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBza2luY2FyZSUyMGRhcmt8ZW58MHx8fHwxNzc1MzYyMDk2fDA&ixlib=rb-4.1.0&q=85",
    title: "Luxury Products",
    category: "Products",
  },
  {
    src: "https://images.unsplash.com/photo-1761718209694-70031ee64f82?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxhZXN0aGV0aWNpYW4lMjBwb3J0cmFpdHxlbnwwfHx8fDE3NzUzNjIwODN8MA&ixlib=rb-4.1.0&q=85",
    title: "Professional Care",
    category: "Treatments",
  },
  {
    src: "https://images.unsplash.com/photo-1749766878223-6ceae855b28b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBzcGElMjBpbnRlcmlvciUyMGRhcmt8ZW58MHx8fHwxNzc1MzYyMDgzfDA&ixlib=rb-4.1.0&q=85",
    title: "Our Facility",
    category: "Facility",
  },
  {
    src: "https://images.pexels.com/photos/3985329/pexels-photo-3985329.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750",
    title: "Skincare Tools",
    category: "Products",
  },
  {
    src: "https://images.pexels.com/photos/3997993/pexels-photo-3997993.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750",
    title: "Treatment Room",
    category: "Facility",
  },
  {
    src: "https://images.pexels.com/photos/3738355/pexels-photo-3738355.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750",
    title: "Facial Massage",
    category: "Treatments",
  },
];

const categories = ["All", "Treatments", "Technology", "Products", "Facility"];

const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.6 }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export const GalleryPage = () => {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const filteredImages =
    selectedCategory === "All"
      ? galleryImages
      : galleryImages.filter((img) => img.category === selectedCategory);

  return (
    <div data-testid="gallery-page" className="pt-20">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059]">
              Gallery
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light text-white mt-4 mb-6">
              A Glimpse Into<br />
              <span className="gold-gradient">Excellence</span>
            </h1>
            <p className="font-body text-lg text-[#A3A3A3] leading-relaxed">
              Explore our state-of-the-art facility, advanced treatments, and 
              the luxurious environment where our students master the art of 
              aesthetic excellence.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filter Tabs */}
      <section className="py-8 bg-[#0A0A0A] border-y border-[#262626]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                data-testid={`filter-${category.toLowerCase()}`}
                className={`font-body text-sm tracking-wider uppercase px-6 py-3 transition-all duration-300 ${
                  selectedCategory === category
                    ? "bg-[#C5A059] text-[#050505]"
                    : "text-[#A3A3A3] hover:text-white border border-[#262626] hover:border-[#C5A059]"
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-16 md:py-24 bg-[#0A0A0A]" data-testid="gallery-grid">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            layout
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            <AnimatePresence mode="popLayout">
              {filteredImages.map((image, index) => (
                <motion.div
                  key={image.src}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.4 }}
                >
                  <AnimatedSection>
                    <div
                      className="gallery-item cursor-pointer aspect-[4/3]"
                      onClick={() => setSelectedImage(image)}
                      data-testid={`gallery-item-${index}`}
                    >
                      <img
                        src={image.src}
                        alt={image.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="gallery-overlay">
                        <div className="text-center">
                          <ZoomIn className="w-8 h-8 text-[#C5A059] mx-auto mb-2" />
                          <span className="font-heading text-xl text-white">
                            {image.title}
                          </span>
                          <span className="block font-body text-xs text-[#A3A3A3] mt-1 tracking-wider uppercase">
                            {image.category}
                          </span>
                        </div>
                      </div>
                    </div>
                  </AnimatedSection>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-[#050505]/95 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
            data-testid="lightbox-modal"
          >
            <button
              className="absolute top-6 right-6 w-12 h-12 border border-[#262626] flex items-center justify-center text-white hover:border-[#C5A059] hover:text-[#C5A059] transition-all duration-300"
              onClick={() => setSelectedImage(null)}
              data-testid="lightbox-close"
            >
              <X size={24} />
            </button>
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="max-w-5xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={selectedImage.src}
                alt={selectedImage.title}
                className="w-full h-auto max-h-[80vh] object-contain"
              />
              <div className="mt-4 text-center">
                <h3 className="font-heading text-2xl text-white">
                  {selectedImage.title}
                </h3>
                <span className="font-body text-sm text-[#C5A059] tracking-wider uppercase">
                  {selectedImage.category}
                </span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default GalleryPage;
